<template>
  <div>
    <div class="col-12">
      <h5 data-cy="application-form-section-landlord">
        {{ $t('message.applicationForm.currentLandlord') }}
      </h5>
    </div>
    <div class="row mb-2">
      <div class="col-12 col-md-6">
        <coozzy-form-checkbox
          v-model="hasNoCurrentLandlord"
          data-cy="application-form-landlord-none-checkbox"
          :initial="hasNoCurrentLandlord">
          {{ $t('message.applicationForm.noCurrentLandlord') }}
        </coozzy-form-checkbox>
      </div>
    </div>
    <div
      v-if="!hasNoCurrentLandlord"
      class="row">
      <div
        class="col-12"
        :class="individualId ? 'col-md-6' : ''">
        <coozzy-form-input
          v-model="landlordName"
          data-cy="application-form-landlord-company-input"
          :name="$t('message.applicationForm.landlordName') + (isCurrentLandLordRequired ? ' *' : '')"
          type="text"
          :state="($v.landlordName && $v.landlordName.$dirty && $v.landlordName.$error) ? false : null" />
      </div>
      <div class="col-12 col-md-6">
        <coozzy-form-input
          v-model="landlordPersonName"
          data-cy="application-form-landlord-contact-input"
          :name="$t('message.generic.contactPerson') + (isCurrentLandLordRequired ? ' *' : '')"
          type="text"
          :state="($v.landlordPersonName && $v.landlordPersonName.$dirty && $v.landlordPersonName.$error) ? false : null" />
      </div>
      <div class="col-12 col-md-6">
        <coozzy-form-input
          v-model="landlordPhone"
          data-cy="application-form-landlord-phone-input"
          :name="$t('message.generic.phoneNumber') + (isCurrentLandLordRequired ? ' *' : '')"
          type="text"
          :state="($v.landlordPhone && $v.landlordPhone.$dirty && $v.landlordPhone.$error) ? false : null" />
      </div>
      <div class="col-12 col-md-6">
        <coozzy-form-input
          v-model="landlordEmail"
          data-cy="application-form-landlord-email-input"
          :name="$t('message.generic.email') + (isCurrentLandLordRequired ? ' *' : '')"
          type="text"
          :state="($v.landlordEmail && $v.landlordEmail.$dirty && $v.landlordEmail.$error) ? false : null" />
      </div>
      <div class="col-12 col-md-6">
        <coozzy-form-input
          ref="rentedSince"
          v-model="dates.rentedSince"
          data-cy="application-form-landlord-rented-since-input"
          :filter="'formatDate'"
          :name="$t('message.applicationForm.rentedSince') + (isCurrentLandLordRequired ? ' *' : '')"
          type="date"
          :state="($v.rentedSince && $v.rentedSince.$dirty && $v.rentedSince.$error) ? false : null"
          @blur="syncRentedSinceFromDateInput()" />
      </div>
      <div class="col-12 col-md-6">
        <!-- <coozzy-form-input
          v-model="previousGrossRent"
          :name="$t('message.applicationForm.previousGrossRent')"
          type="text" /> -->
        <coozzy-form-input-numeric
          v-model="previousGrossRent"
          data-cy="application-form-landlord-previous-gross-rent-input"
          type="number"
          :filter="'formatPrice'"
          :min="0"
          placeholder="CHF"
          :is-decimal="true"
          :check-valide="$v.previousGrossRent && $v.previousGrossRent.$dirty && $v.previousGrossRent.$error ? 'is-invalid' : ''"
          :name="$t('message.applicationForm.previousGrossRent') + (isCurrentLandLordRequired ? ' *' : '')" />
      </div>
      <div class="col-12 col-md-6">
        <label :for="`cancelledByTenant${individualId}`">{{ $t('message.applicationForm.cancelledByTenant') + (isCurrentLandLordRequired ? ' *' : '') }}</label>
        <coozzy-form-select
          :id="`cancelledByTenant${individualId}`"
          v-model="cancelledByTenant"
          data-cy="application-form-landlord-cancelled-by-tenant-select"
          :state="($v.cancelledByTenant && $v.cancelledByTenant.$dirty && $v.cancelledByTenant.$error) ? false : null">
          <option value="UNDEFINED_VALUE">
            -
          </option>
          <option value="APPLIES">
            {{ $t('message.generic.selectField.yes') }}
          </option>
          <option value="DOES_NOT_APPLY">
            {{ $t('message.generic.selectField.no') }}
          </option>
        </coozzy-form-select>
      </div>
      <div class="col-12 col-md-6">
        <coozzy-form-input
          v-model="relocationReason"
          data-cy="application-form-landlord-relocation-reason-input"
          :name="$t('message.applicationForm.relocationReason') + (isCurrentLandLordRequired ? ' *' : '')"
          type="text"
          :state="($v.relocationReason && $v.relocationReason.$dirty && $v.relocationReason.$error) ? false : null" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import { mapGetters, mapMutations } from 'vuex'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import { validation, isEmailValid } from '@/mixins/validation'
import { required, not } from 'vuelidate/lib/validators'
import CoozzyFormInputNumeric from '@/framework/components/form/input/CoozzyFormInputNumeric.vue'
import {
  ApplicationFormCompany,
  ApplicationFormIndividual,
  ApplicationFormTenancy,
  ApplicationFormTenancyDataState,
  ApplicationFormTenancyItem,
  ObjectWithCategory,
  TypeDateLike
} from '@/types/models'

export default Vue.extend({
  name: 'ApplicationFormTenancy',
  components: { CoozzyFormInputNumeric, CoozzyFormSelect, CoozzyFormCheckbox, CoozzyFormInput },
  mixins: [validation],
  props: {
    company: {
      type: Object as PropType<ApplicationFormCompany | null>,
      default: null
    },
    individualId: {
      type: String,
      default: ''
    },
    sending: {
      type: Boolean,
      default: false
    },
    object: {
      type: Object as PropType<ObjectWithCategory | null>,
      default: null
    }
  },
  data(): ApplicationFormTenancyDataState {
    return {
      dates: {
        rentedSince: null
      }
    }
  },
  computed: {
    cancelledByTenant: {
      get(): ApplicationFormTenancy['cancelledByTenant'] {
        return this.tenancySubject.tenancy.cancelledByTenant
      },
      set(value: ApplicationFormTenancy['cancelledByTenant']) {
        const subject = this.tenancySubject
        subject.tenancy.cancelledByTenant = value
        this.updateTenancySubject(subject)
      }
    },
    hasNoCurrentLandlord: {
      get(): boolean {
        return !this.tenancySubject.tenancy.hasCurrentLandlord
      },
      set(value: boolean) {
        const subject = this.tenancySubject
        subject.tenancy.hasCurrentLandlord = !value
        this.updateTenancySubject(subject)
      }
    },
    ...mapGetters('applicationForm', ['getIndividualByInternalId']),
    tenancySubject(): ApplicationFormTenancyItem {
      if (this.company !== null) {
        return this.company
      }
      return this.getIndividualByInternalId(this.individualId) as ApplicationFormIndividual
    },
    isMainIndividual() {
      return Boolean((this.tenancySubject as ApplicationFormIndividual).mainIndividual)
    },
    landlordEmail: {
      get(): string {
        return this.tenancySubject.tenancy.landlordEmail
      },
      set(value: string) {
        const subject = this.tenancySubject
        subject.tenancy.landlordEmail = value
        this.updateTenancySubject(subject)
      }
    },
    landlordName: {
      get(): string {
        return this.tenancySubject.tenancy.landlordName
      },
      set(value: string) {
        const subject = this.tenancySubject
        subject.tenancy.landlordName = value
        this.updateTenancySubject(subject)
      }
    },
    landlordPersonName: {
      get(): string {
        return this.tenancySubject.tenancy.landlordPersonName
      },
      set(value: string) {
        const subject = this.tenancySubject
        subject.tenancy.landlordPersonName = value
        this.updateTenancySubject(subject)
      }
    },
    landlordPhone: {
      get(): string {
        return this.tenancySubject.tenancy.landlordPhone
      },
      set(value: string) {
        const subject = this.tenancySubject
        subject.tenancy.landlordPhone = value
        this.updateTenancySubject(subject)
      }
    },
    relocationReason: {
      get(): string {
        return this.tenancySubject.tenancy.relocationReason
      },
      set(value: string) {
        const subject = this.tenancySubject
        subject.tenancy.relocationReason = value
        this.updateTenancySubject(subject)
      }
    },
    rentedSince: {
      get(): ApplicationFormTenancy['rentedSince'] | null {
        return this.tenancySubject.tenancy.rentedSince !== '-1'
          ? this.tenancySubject.tenancy.rentedSince
          : null
      },
      set(value: ApplicationFormTenancy['rentedSince']) {
        const subject = this.tenancySubject
        subject.tenancy.rentedSince = value
        this.updateTenancySubject(subject)
      }
    },
    previousGrossRent: {
      get(): number | null {
        return this.tenancySubject.tenancy.previousGrossRent !== -1
          ? this.tenancySubject.tenancy.previousGrossRent
          : null
      },
      set(value: number) {
        const subject = this.tenancySubject
        subject.tenancy.previousGrossRent = value
        this.updateTenancySubject(subject)
      }
    },
    isCurrentLandLordRequired() {
      return !this.hasNoCurrentLandlord && this.object && ['HOUSE', 'APARTMENT', 'COMMERCIAL', 'GASTRONOMY'].includes(this.object.category)
    }
  },
  mounted() {
    const rentedSince = this.rentedSince as TypeDateLike | null
    this.dates.rentedSince = rentedSince ? `${rentedSince.year}-${rentedSince.month.toString().padStart(2, '0')}-${rentedSince.day.toString().padStart(2, '0')}` : null
  },
  methods: {
    ...mapMutations('applicationForm', ['updateIndividual', 'updateCompany']),
    updateTenancySubject(subject: ApplicationFormTenancyItem) {
      if (this.individualId) {
        this.updateIndividual(subject)
      } else {
        this.updateCompany(subject)
      }
    },
    syncRentedSinceFromDateInput() {
      const date = new Date(this.dates.rentedSince)
      this.rentedSince = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
      }
    },
    isInvalid() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.scrollToError()
        return true
      }
      return false
    }
  },
  validations() {
    if (!this.isCurrentLandLordRequired) {
      return {}
    }

    return {
      landlordName: { required },
      landlordPersonName: { required },
      landlordPhone: { required },
      landlordEmail: { required, isEmailValid },
      rentedSince: { required },
      previousGrossRent: { required },
      cancelledByTenant: {
        required,
        notUndefined: not((value: string) => value === 'UNDEFINED_VALUE')
      },
      relocationReason: { required }
    }
  }
})
</script>

<style lang="scss" scoped>
.custom-radio {
  display: inline-block;
}
</style>
